/*
Copyright (c) 2017 Dropbox, Inc.

Licensed under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/

import { Subject } from "rxjs";

const EVENTS_ARRAY = [
  "keypress",
  "keydown",
  "click",
  "contextmenu",
  "dblclick",
  "mousemove",
  "scroll",
  "touchmove",
  "touchstart",
];

export const enum ReportMode {
  Standard = 1,
  Advanced = 2,
}

const DEFAULT_ACTIVITY_REPORT_IN_SEC = 5;

export interface Options {
  activityReportInSec?: number;
  activityReportMode?: ReportMode;
  detectFramesInSec?: number;
  onActivity?: () => void;
  onEnterFrame?: () => void;
  onReportUserIsActive?: () => void;
  onReportUserIsIdle?: () => void;
}

export class Idle {
  userReportedActive: boolean = false; // used for advanced mode
  userIsActive: boolean = false; //used for standard mode
  userMouseIsInFrame: boolean = false;
  attachedFrames: HTMLElement[] = [];
  activityReportMode: ReportMode = ReportMode.Advanced;
  activityReportInSec: number = DEFAULT_ACTIVITY_REPORT_IN_SEC;
  // Add near other fields
  private activitySampleTimer: number | NodeJS.Timeout | null = null;
  private activityPending = false;
  detectFramesInSec: number = -1;
  samplePeriod = 1000;
  onActivity: () => void = () => {};
  onEnterFrame: () => void = () => {};
  onReportUserIsActive: () => void = () => {};
  onReportUserIsIdle: () => void = () => {};
  attachedWindows: Window[] = [];
  intervalId: number | NodeJS.Timeout | null = null;
  detectedFramesIntervalId: number | NodeJS.Timeout | null = null;
  ac;
  static pending;

  private root = {
    setTimeout: (...args: Parameters<typeof window.setTimeout>) =>
      (window as any).__zone_symbol__setTimeout.apply(window, args),
    clearTimeout: (...args: Parameters<typeof window.clearTimeout>) =>
      (window as any).__zone_symbol__clearTimeout.apply(window, args),
  };

  private static singleton: Idle;
  static superseded: boolean = false;
  userActivitySubject: Subject<void> = new Subject<void>();

  static addNativeWindow(wnd: Window) {
    if (Idle.singleton != null) {
      Idle.singleton.attachListenerToNewWindow(wnd);
    } else if (!Idle.superseded) {
      if (Idle.pending == null) {
        Idle.pending = new Set<Window>();
      }
      Idle.pending.add(wnd);
    }
  }

  constructor(options: Options = {}) {
    if (Idle.superseded) {
      return null;
    } else if (Idle.singleton == null) {
      Idle.singleton = this;
    } else {
      return Idle.singleton;
    }

    this.ac = new AbortController();
    this.overrideDefaultsWithOptions(options);

    EVENTS_ARRAY.forEach((event) => {
      window.addEventListener(event, this.onUserActivity, {
        passive: true,
        signal: this.ac.signal,
      });
    });

    this.scheduleIdleTick(this.activityReportInSec * 1000);

    if (this.detectFramesInSec > 0) {
      this.scheduleFrameDetection(this.detectFramesInSec * 1000);
    }

    if (Idle.pending != null && Idle.pending.size > 0) {
      for (let wnd of Idle.pending.values()) {
        this.attachListenerToNewWindow(wnd);
      }

      Idle.pending.clear();
    }

    // activity signal is sent when the page loaded
    this.reportActivity();
  }

  attachListenerToNewFrames = () => {
    const frames = Array.prototype.slice.call(window.document.getElementsByTagName("frame"));
    const iframes = Array.prototype.slice.call(window.document.getElementsByTagName("iframe"));
    const allFrames = frames.concat(iframes);
    for (const frame of allFrames) {
      if (this.attachedFrames.indexOf(frame) < 0) {
        frame.addEventListener("mouseenter", this.onMouseEntersFrame.bind(this), this, {
          passive: true,
          signal: this.ac.signal,
        });
        this.attachedFrames.push(frame);
        // We can't know if the mouse is within the new frame already, so we guess it is.
        // If its not we would find out upon any user activity
        this.userMouseIsInFrame = true;
      }
    }
  };

  attachListenerToNewWindow = (wnd) => {
    if (this.attachedWindows.includes(wnd)) {
      return;
    }

    EVENTS_ARRAY.forEach((event) => {
      wnd.addEventListener(event, this.onUserActivity, { passive: true, signal: this.ac.signal });
    });

    this.attachedWindows.push(wnd);
    // We can't know if the mouse is within the new frame already, so we guess it is.
    // If its not we would find out upon any user activity
    this.userMouseIsInFrame = true;
  };

  onUserActivity = () => {
    // Flag that there is pending activity to be sampled
    this.userActivitySubject.next(); // Keep your existing Subject if other observers rely on it
    this.activityPending = true;

    // Arm a one-shot sampler if not already scheduled
    if (this.activitySampleTimer == null) {
      this.scheduleActivitySample(this.samplePeriod);
    }
  };

  /**
   * this is relatively expensive, compared to native IdleDetector
   */
  sampleUserActivity = () => {
    this.onActivity();
    this.userIsActive = true;

    if (this.activityReportMode === ReportMode.Advanced && !this.userReportedActive) {
      this.reportActivity();
    }
    // user is active => user is not in a frame
    this.userMouseIsInFrame = false;
  };

  public onMouseEntersFrame() {
    this.onEnterFrame();
    this.userMouseIsInFrame = true;
  }

  private onTimerTick() {
    this.userReportedActive = false;
    if (
      (this.activityReportMode === ReportMode.Standard && this.userIsActive) ||
      (this.userMouseIsInFrame && this.isPageVisible())
    ) {
      this.reportActivity();
    } else {
      this.onReportUserIsIdle();
    }
  }

  private reportActivity() {
    this.onReportUserIsActive();
    this.userReportedActive = true;
    this.userIsActive = false;
  }

  private overrideDefaultsWithOptions(options: Options) {
    this.activityReportInSec = options.activityReportInSec || this.activityReportInSec;
    this.activityReportMode = options.activityReportMode || this.activityReportMode;
    this.detectFramesInSec = options.detectFramesInSec || this.detectFramesInSec;
    this.onActivity = options.onActivity || this.onActivity;
    this.onEnterFrame = options.onEnterFrame || this.onEnterFrame;
    this.onReportUserIsActive = options.onReportUserIsActive || this.onReportUserIsActive;
    this.onReportUserIsIdle = options.onReportUserIsIdle || this.onReportUserIsIdle;
  }

  public isPageVisible() {
    // return true for "visible" / "prerender" / "undefined" (unsupported browsers)
    return document.visibilityState !== "hidden";
  }

  public destroy() {
    // added for safe teardown. if any more options are customized please add here. -dc
    this.activityReportInSec = DEFAULT_ACTIVITY_REPORT_IN_SEC;
    this.onReportUserIsActive = function () {
      return {};
    }.bind(this);
    this.onReportUserIsIdle = function () {
      return {};
    }.bind(this);
    this.onActivity = function () {
      return {};
    }.bind(this);
    this.ac.abort();
    this.ac = null;
    Idle.singleton = null;

    if (this.intervalId != null) this.root.clearTimeout(this.intervalId);
    if (this.detectedFramesIntervalId != null)
      this.root.clearTimeout(this.detectedFramesIntervalId);

    if (this.activitySampleTimer != null) {
      this.root.clearTimeout(this.activitySampleTimer);
      this.activitySampleTimer = null;
    }
  }

  private scheduleIdleTick(delayMs = this.activityReportInSec * 1000) {
    if (this.intervalId != null) this.root.clearTimeout(this.intervalId);
    const jitter = (Math.random() - 0.5) * delayMs * 0.1;
    const nextDelay = Math.max(0, delayMs + jitter);

    this.intervalId = this.root.setTimeout(() => {
      this.intervalId = null;
      this.onTimerTick();
      this.scheduleIdleTick(this.activityReportInSec * 1000);
    }, nextDelay);
  }

  private scheduleFrameDetection(delayMs = this.detectFramesInSec * 1000) {
    if (this.detectedFramesIntervalId != null)
      this.root.clearTimeout(this.detectedFramesIntervalId);

    const jitter = (Math.random() - 0.5) * delayMs * 0.1;
    const nextDelay = Math.max(0, delayMs + jitter);

    this.detectedFramesIntervalId = this.root.setTimeout(() => {
      this.detectedFramesIntervalId = null;
      this.attachListenerToNewFrames();
      this.scheduleFrameDetection(this.detectFramesInSec * 1000);
    }, nextDelay);
  }

  private scheduleActivitySample(delayMs: number) {
    // Small jitter to avoid perfectly regular cadence if many windows are active
    const jitter = (Math.random() - 0.5) * Math.min(50, delayMs * 0.1); // cap jitter at 50ms
    const nextDelay = Math.max(0, Math.floor(delayMs + jitter));

    this.activitySampleTimer = this.root.setTimeout(() => this.drainActivitySample(), nextDelay);
  }

  private drainActivitySample() {
    this.activitySampleTimer = null;

    // If we saw at least one event in the last window, run the sampler once
    if (this.activityPending) {
      this.activityPending = false;
      this.sampleUserActivity(); // <- your existing method; unchanged
    }

    // If more events landed during the execution above, schedule another window
    if (this.activityPending) {
      this.scheduleActivitySample(this.samplePeriod);
    }
  }
}
